import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import './SkillsPopup.css';

const SkillsPopup = ({ isOpen, onClose, onSave, userSkills = [] }) => {
  const [availableSkills, setAvailableSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState('category'); // category, subcategory, skills

  // Fetch available skills and categories
  useEffect(() => {
    if (isOpen) {
      fetchSkillsAndCategories();
      // Initialize selected skills with current user skills
      const existingSkillIds = userSkills.map(userSkill => userSkill.skill?.id || userSkill.id);
      setSelectedSkills(existingSkillIds);
      
      // If user has existing skills, try to determine the category and sub-category
      if (existingSkillIds.length > 0) {
        // We'll set this after skills are loaded
        setCurrentStep('category');
      } else {
        setCurrentStep('category');
      }
      
      setSelectedCategory('');
      setSelectedSubCategory('');
    }
  }, [isOpen, userSkills]);

  const fetchSkillsAndCategories = async () => {
    setLoading(true);
    try {
      const [skillsResponse, categoriesResponse] = await Promise.all([
        ApiService.get('/skills'),
        ApiService.get('/categories')
      ]);

      if (skillsResponse.success && categoriesResponse.success) {
        // Handle paginated skills response
        const skillsData = skillsResponse.data?.data || skillsResponse.data || [];
        const categoriesData = categoriesResponse.data?.data || categoriesResponse.data || [];
        
        setAvailableSkills(skillsData);
        setCategories(categoriesData);
        
        // Extract sub-categories from categories data
        const subCats = categoriesData.filter(cat => cat.parent_id !== null);
        setSubCategories(subCats);
        
        // If user has existing skills, try to navigate to the appropriate step
        if (userSkills.length > 0) {
          handleSmartNavigation(skillsData, categoriesData, subCats);
        }
      } else {
        setError('Failed to load skills data');
      }
    } catch (err) {
      setError('Error loading skills: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSmartNavigation = (skillsData, categoriesData, subCats) => {
    const existingSkillIds = userSkills.map(userSkill => userSkill.skill?.id || userSkill.id);
    const userSkillsData = skillsData.filter(skill => existingSkillIds.includes(skill.id));
    
    if (userSkillsData.length > 0) {
      // Find the most common category among user's skills
      const categoryCounts = {};
      const subCategoryCounts = {};
      
      userSkillsData.forEach(skill => {
        if (skill.category_id) {
          categoryCounts[skill.category_id] = (categoryCounts[skill.category_id] || 0) + 1;
        }
        if (skill.sub_category_id) {
          subCategoryCounts[skill.sub_category_id] = (subCategoryCounts[skill.sub_category_id] || 0) + 1;
        }
      });
      
      // Set the most common category
      const mostCommonCategory = Object.keys(categoryCounts).reduce((a, b) => 
        categoryCounts[a] > categoryCounts[b] ? a : b
      );
      
      if (mostCommonCategory) {
        setSelectedCategory(mostCommonCategory);
        
        // Check if there's a most common sub-category
        const mostCommonSubCategory = Object.keys(subCategoryCounts).reduce((a, b) => 
          subCategoryCounts[a] > subCategoryCounts[b] ? a : b
        );
        
        if (mostCommonSubCategory) {
          setSelectedSubCategory(mostCommonSubCategory);
          setCurrentStep('skills');
        } else {
          setCurrentStep('subcategory');
        }
      }
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubCategory('');
    setCurrentStep('subcategory');
  };

  const handleSubCategorySelect = (subCategoryId) => {
    setSelectedSubCategory(subCategoryId);
    setCurrentStep('skills');
  };

  const handleBackToCategory = () => {
    setCurrentStep('category');
    setSelectedCategory('');
    setSelectedSubCategory('');
  };

  const handleBackToSubCategory = () => {
    setCurrentStep('subcategory');
    setSelectedSubCategory('');
  };

  const handleSkillToggle = (skillId) => {
    setSelectedSkills(prev => 
      prev.includes(skillId) 
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await ApiService.put('/user-skills/bulk', {
        skill_ids: selectedSkills
      });

      if (response.success) {
        // Ensure response.data is properly structured
        const skillsData = response.data || [];
        
        // Update localStorage auth_user data with new skills
        const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
        if (authUser && authUser.id) {
          authUser.skills = skillsData;
          localStorage.setItem('auth_user', JSON.stringify(authUser));
        }
        
        onSave(skillsData);
        onClose();
      } else {
        setError(response.message || 'Failed to save skills');
      }
    } catch (err) {
      setError('Error saving skills: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const filteredSkills = availableSkills.filter(skill => {
    const matchesSearch = skill.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || skill.category_id == selectedCategory;
    const matchesSubCategory = !selectedSubCategory || skill.sub_category_id == selectedSubCategory;
    return matchesSearch && matchesCategory && matchesSubCategory;
  });

  const getFilteredSubCategories = () => {
    if (!selectedCategory) return [];
    return subCategories.filter(subCat => subCat.parent_id == selectedCategory);
  };

  const getSkillCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const getSkillSubCategoryName = (subCategoryId) => {
    const subCategory = categories.find(cat => cat.id === subCategoryId);
    return subCategory ? subCategory.name : '';
  };

  const getSelectedCategoryName = () => {
    return getSkillCategoryName(selectedCategory);
  };

  const getSelectedSubCategoryName = () => {
    return getSkillSubCategoryName(selectedSubCategory);
  };

  if (!isOpen) return null;

  return (
    <div className="skills-popup-overlay">
      <div className="skills-popup">
        <div className="skills-popup-header">
          <div className="skills-popup-title">
            <h3>Add Your Skills & Expertise</h3>
            <p>
              {currentStep === 'category' && 'Choose a category to explore skills'}
              {currentStep === 'subcategory' && `Choose a sub-category in ${getSelectedCategoryName()}`}
              {currentStep === 'skills' && `Select skills from ${getSelectedCategoryName()} - ${getSelectedSubCategoryName()}`}
            </p>
          </div>
          <button className="skills-popup-close" onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="skills-popup-content">
          {error && (
            <div className="skills-popup-error">
              <i className="bi bi-exclamation-triangle"></i>
              {error}
            </div>
          )}

          {/* Breadcrumb Navigation */}
          <div className="skills-breadcrumb">
            <button 
              className={`breadcrumb-item ${currentStep === 'category' ? 'active' : ''}`}
              onClick={() => setCurrentStep('category')}
            >
              <i className="bi bi-tags"></i>
              Category
            </button>
            {selectedCategory && (
              <>
                <i className="bi bi-chevron-right"></i>
                <button 
                  className={`breadcrumb-item ${currentStep === 'subcategory' ? 'active' : ''}`}
                  onClick={() => setCurrentStep('subcategory')}
                >
                  <i className="bi bi-diagram-3"></i>
                  Sub-category
                </button>
              </>
            )}
            {selectedSubCategory && (
              <>
                <i className="bi bi-chevron-right"></i>
                <button 
                  className={`breadcrumb-item ${currentStep === 'skills' ? 'active' : ''}`}
                  onClick={() => setCurrentStep('skills')}
                >
                  <i className="bi bi-star"></i>
                  Skills
                </button>
              </>
            )}
          </div>

          {/* Search and Filters - Only show in skills step */}
          {currentStep === 'skills' && (
            <div className="skills-popup-filters">
              <div className="skills-search">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  placeholder="Search skills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="skills-popup-body">
            {loading ? (
              <div className="skills-loading">
                <div className="spinner-enhanced"></div>
                <p>Loading your skills...</p>
                <small>Please wait while we fetch the latest data</small>
              </div>
            ) : (
              <>
                {/* Category Selection Step */}
                {currentStep === 'category' && (
                  <div className="categories-grid">
                    {categories.filter(cat => cat.parent_id === null).map(category => (
                      <div
                        key={category.id}
                        className="category-item"
                        onClick={() => handleCategorySelect(category.id)}
                      >
                        <div className="category-icon">
                          <i className="bi bi-tags"></i>
                        </div>
                        <div className="category-info">
                          <div className="category-name">{category.name}</div>
                          <div className="category-description">
                            {category.description || 'Explore skills in this category'}
                          </div>
                        </div>
                        <div className="category-arrow">
                          <i className="bi bi-chevron-right"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Sub-category Selection Step */}
                {currentStep === 'subcategory' && (
                  <div className="subcategories-grid">
                    <div className="back-button" onClick={handleBackToCategory}>
                      <i className="bi bi-arrow-left"></i>
                      Back to Categories
                    </div>
                    {getFilteredSubCategories().map(subCategory => (
                      <div
                        key={subCategory.id}
                        className="subcategory-item"
                        onClick={() => handleSubCategorySelect(subCategory.id)}
                      >
                        <div className="subcategory-icon">
                          <i className="bi bi-diagram-3"></i>
                        </div>
                        <div className="subcategory-info">
                          <div className="subcategory-name">{subCategory.name}</div>
                          <div className="subcategory-description">
                            {subCategory.description || 'Select skills from this sub-category'}
                          </div>
                        </div>
                        <div className="subcategory-arrow">
                          <i className="bi bi-chevron-right"></i>
                        </div>
                      </div>
                    ))}
                    {getFilteredSubCategories().length === 0 && (
                      <div className="no-subcategories">
                        <i className="bi bi-info-circle"></i>
                        <p>No sub-categories available for this category</p>
                        <button 
                          className="btn-primary"
                          onClick={() => setCurrentStep('skills')}
                        >
                          View All Skills
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Skills Selection Step */}
                {currentStep === 'skills' && (
                  <>
                    <div className="back-button" onClick={handleBackToSubCategory}>
                      <i className="bi bi-arrow-left"></i>
                      Back to Sub-categories
                    </div>
                    <div className="skills-grid">
                      {filteredSkills.map(skill => (
                        <div
                          key={skill.id}
                          className={`skill-item ${selectedSkills.includes(skill.id) ? 'selected' : ''}`}
                          onClick={() => handleSkillToggle(skill.id)}
                        >
                          <div className="skill-checkbox">
                            <i className={`bi ${selectedSkills.includes(skill.id) ? 'bi-check-square-fill' : 'bi-square'}`}></i>
                          </div>
                          <div className="skill-info">
                            <div className="skill-name">{skill.name}</div>
                            {skill.description && (
                              <div className="skill-description">{skill.description}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {filteredSkills.length === 0 && !loading && (
                      <div className="skills-empty">
                        <i className="bi bi-search"></i>
                        <p>No skills found matching your search</p>
                        <small>Try adjusting your search terms</small>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="skills-popup-footer">
            {currentStep === 'skills' && (
              <div className="selected-count">
                {selectedSkills.length} skill{selectedSkills.length !== 1 ? 's' : ''} selected
              </div>
            )}
            <div className="skills-popup-actions">
              <button className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              {currentStep === 'skills' && (
                <button 
                  className="btn-save" 
                  onClick={handleSave}
                  disabled={saving || selectedSkills.length === 0}
                >
                  {saving ? (
                    <>
                      <div className="spinner-small"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg"></i>
                      Save
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillsPopup;
